import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalProducts from "@faire/web-api--source/routes/www/brand-portal/my-shop/products";
import brandPortalProductsBulkUploader from "@faire/web-api--source/routes/www/brand-portal/my-shop/products/bulk-uploader";
import brandPortalMyShopSettings from "@faire/web-api--source/routes/www/brand-portal/my-shop/settings";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const ProductTranslationsIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="If you sell your products overseas, you can write custom translations for your product descriptions."
          description={{
            text: "Introduction to customizing product translations",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="To set up custom translations, follow these steps:"
          description={{
            text: "Header for steps to set up custom translations",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Go to <LinkFormatting>Shop settings</LinkFormatting> in your brand portal under <BoldFormatting>Settings</BoldFormatting>."
              values={{
                LinkFormatting: ShopSettingsLinkFormatting,
                BoldFormatting,
              }}
              description={{
                text: "First step to set up custom translations",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select <BoldFormatting>Custom catalog translations</BoldFormatting>."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Second step to set up custom translations",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Choose the languages you want to add custom translations for. You must select these languages before editing your products."
              description={{
                text: "Third step to set up custom translations",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Once you’ve selected the languages for custom descriptions, you can update your products using either the <BulkUploaderLinkFormatting>Bulk Uploader</BulkUploaderLinkFormatting> or the <BoldFormatting>Product Editor</BoldFormatting> found on the <ProductsLinkFormatting>Products</ProductsLinkFormatting> page."
              values={{
                BulkUploaderLinkFormatting,
                ProductsLinkFormatting,
                BoldFormatting,
              }}
              description={{
                text: "Fourth step to set up custom translations",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="If a custom translation isn’t provided, we’ll automatically translate your listings into supported languages. If you delete a language after selecting it, any custom translations you created for that language will be permanently deleted. Re-adding a language will require you to provide the custom translation again."
          description={{
            text: "Information about automatic translations and deletion",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Some edits, including text formatting and special characters in product descriptions, will need to be made by editing individual product listings. If the issue persists, contact our support team for assistance."
          description={{
            text: "Additional information about editing product descriptions",
          }}
        />
      </Typography>
    </Column>
  );
};

const ShopSettingsLinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    linkTo={brandPortalMyShopSettings()}
    target="_blank"
  >
    <Bold>{chunks}</Bold>
  </Button>
);

const ProductsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalProducts()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const BulkUploaderLinkFormatting = (chunks: JSX.Element) => (
  <Button
    variant="tertiary"
    linkTo={brandPortalProductsBulkUploader()}
    target="_blank"
  >
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
